/* eslint-disable relay/unused-fields -- queue edges are consumed downstream by
   the shared widget components via their own fragments (urlAlias, covers). */
import { type FunctionComponent, type ReactNode, Suspense, useEffect } from "react";
import type { PreloadedQuery } from "react-relay";
import { graphql, usePreloadedQuery, useQueryLoader } from "react-relay";

import type { fallbackQueryLoaderQuery } from "scmp-app/queries/__generated__/fallbackQueryLoaderQuery.graphql";

import type { FallbackQueryVariables } from "./index";

const fallbackQueryLoaderQueryNode = graphql`
  query fallbackQueryLoaderQuery(
    $chinaFutureTechSectionEntityId: String!
    $chinaFutureTechStoryPackageArticlesQueueName: String!
    $storyPackageTwoQueueName: String!
    $packageTopStoryPackageArticlesQueueName: String!
    $spotlightQueueName: String!
    $includeStoryPackageOne: Boolean!
    $includeStoryPackageTwo: Boolean!
  ) {
    ...queueTopicHooksHomepageMidPageQueueTopicQuery
      @arguments(
        packageTopQueueName: $packageTopStoryPackageArticlesQueueName
        storyPackageTwoQueueName: $storyPackageTwoQueueName
      )
    packageTopQueue: queue(filter: { name: $packageTopStoryPackageArticlesQueueName })
      @include(if: $includeStoryPackageOne) {
      items(first: 4) {
        edges {
          node {
            ... on Content {
              entityId
              urlAlias
              ...contentCoverImageContent
                @arguments(withLandscape250x99: true, withSize540x360: true)
              ...homeSecondaryV2ContentItemContent
              ...entityLink
            }
          }
        }
      }
    }
    storyPackageTwoQueue: queue(filter: { name: $storyPackageTwoQueueName })
      @include(if: $includeStoryPackageTwo) {
      items(first: 4) {
        edges {
          node {
            ... on Content {
              entityId
              urlAlias
              ...contentCoverImageContent @arguments(withSize540x360: true, withSize1200x800: true)
              ...homeSecondaryV2ContentItemContent
              ...entityLink
            }
          }
        }
      }
    }
    ...chinaFutureTechStoryPackageHomeV2Query
      @arguments(
        chinaFutureTechSectionEntityId: $chinaFutureTechSectionEntityId
        chinaFutureTechStoryPackageArticlesQueueName: $chinaFutureTechStoryPackageArticlesQueueName
      )
    ...spotlightHomeV2Query @arguments(spotlightQueueName: $spotlightQueueName)
    packageTopWidgetEntityIds: queue(filter: { name: $packageTopStoryPackageArticlesQueueName })
      @include(if: $includeStoryPackageOne) {
      items(first: 4) {
        edges {
          node {
            ... on Content {
              entityId
            }
          }
        }
      }
    }
    storyPackageTwoWidgetEntityIds: queue(filter: { name: $storyPackageTwoQueueName })
      @include(if: $includeStoryPackageTwo) {
      items(first: 4) {
        edges {
          node {
            ... on Content {
              entityId
            }
          }
        }
      }
    }
    cftWidgetEntityIds: queue(filter: { name: $chinaFutureTechStoryPackageArticlesQueueName }) {
      items(first: 4) {
        edges {
          node {
            ... on Content {
              entityId
            }
          }
        }
      }
    }
    spotlightWidgetEntityIds: queue(filter: { name: $spotlightQueueName }) {
      items(first: 4) {
        edges {
          node {
            ... on Content {
              entityId
            }
          }
        }
      }
    }
  }
`;

type Props = {
  children: (queryReference: QueryResponse) => ReactNode;
  onEntityIds: (entityIds: string[]) => void;
  renderPlaceholders: () => ReactNode;
  variables: FallbackQueryVariables;
};

type QueryResponse = fallbackQueryLoaderQuery["response"];

export const AiWidgetFallbackLoader: FunctionComponent<Props> = ({
  children,
  onEntityIds,
  renderPlaceholders,
  variables,
}) => {
  const [queryReference, loadQuery, disposeQuery] = useQueryLoader<fallbackQueryLoaderQuery>(
    fallbackQueryLoaderQueryNode,
  );

  useEffect(() => {
    loadQuery(variables, { fetchPolicy: "network-only" });
    return () => disposeQuery();
  }, [variables, loadQuery, disposeQuery]);

  if (!queryReference) {
    return <>{renderPlaceholders()}</>;
  }

  return (
    <Suspense fallback={<>{renderPlaceholders()}</>}>
      <AiWidgetFallbackContent
        node={fallbackQueryLoaderQueryNode}
        onEntityIds={onEntityIds}
        queryReference={queryReference}
      >
        {children}
      </AiWidgetFallbackContent>
    </Suspense>
  );
};

AiWidgetFallbackLoader.displayName = "AiWidgetFallbackLoader";

const AiWidgetFallbackContent: FunctionComponent<{
  children: (queryReference: QueryResponse) => ReactNode;
  node: typeof fallbackQueryLoaderQueryNode;
  onEntityIds: (entityIds: string[]) => void;
  queryReference: PreloadedQuery<fallbackQueryLoaderQuery>;
}> = ({ children, node, onEntityIds, queryReference }) => {
  const data = usePreloadedQuery(node, queryReference);

  useEffect(() => {
    const entityIds = [
      ...(data.packageTopWidgetEntityIds?.items?.edges ?? []),
      ...(data.storyPackageTwoWidgetEntityIds?.items?.edges ?? []),
      ...(data.cftWidgetEntityIds?.items?.edges ?? []),
      ...(data.spotlightWidgetEntityIds?.items?.edges ?? []),
    ]
      .map(edge => edge?.node?.entityId)
      .filter((id): id is string => !!id);
    onEntityIds(entityIds);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  return children(data);
};

AiWidgetFallbackContent.displayName = "AiWidgetFallbackContent";
